import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Lock, Unlock, RotateCw, Trash, Plus, FlaskConical } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, fmtINR } from "../../lib/kostra";

const RESPONSE_FIELDS = [
  { key: "taste", label: "Taste", min: 0, max: 10 },
  { key: "texture", label: "Texture", min: 0, max: 10 },
  { key: "appearance", label: "Appearance", min: 0, max: 10 },
  { key: "yield_pct", label: "Yield %", min: 0, max: 100 },
  { key: "shelf_life_days", label: "Shelf life d", min: 0, max: 60 },
  { key: "overall", label: "Overall", min: 0, max: 10 },
];

export default function TrialsTable({ project, onChange }) {
  const factors = project.factors || [];
  const trials = project.trials || [];
  const [editingNotesIdx, setEditingNotesIdx] = useState(null);
  const [autoCostBusy, setAutoCostBusy] = useState({});
  const debounceRef = useRef({});

  // Auto-recompute cost when levels change and factor has a binding to a recipe.
  const liveCost = async (idx) => {
    const t = trials[idx];
    if (!project.base_recipe_snapshot) return;
    setAutoCostBusy((s) => ({ ...s, [idx]: true }));
    try {
      const { data } = await api.post("/rnd/recompute-cost", {
        base_recipe: project.base_recipe_snapshot,
        factors,
        levels: t.levels || {},
      });
      const next = [...trials];
      const r = { ...(next[idx].results || {}) };
      r.cost = +Number(data.cost_per_portion || 0).toFixed(2);
      r._cost_auto = true;
      next[idx] = { ...next[idx], results: r };
      onChange({ trials: next });
    } catch {} finally {
      setAutoCostBusy((s) => ({ ...s, [idx]: false }));
    }
  };

  const updateLevel = (idx, factorName, value) => {
    const next = [...trials];
    const v = value === "" ? "" : parseFloat(value);
    next[idx] = { ...next[idx], levels: { ...(next[idx].levels || {}), [factorName]: isNaN(v) ? value : v } };
    onChange({ trials: next });
    // debounce live-cost
    clearTimeout(debounceRef.current[idx]);
    debounceRef.current[idx] = setTimeout(() => liveCost(idx), 380);
  };

  const updateResult = (idx, key, value) => {
    const next = [...trials];
    const r = { ...(next[idx].results || {}) };
    if (value === "" || value === null) {
      delete r[key];
    } else {
      r[key] = parseFloat(value);
    }
    if (key === "cost") r._cost_auto = false;
    next[idx] = { ...next[idx], results: r };
    onChange({ trials: next });
  };

  const updateNotes = (idx, val) => {
    const next = [...trials];
    next[idx] = { ...next[idx], notes: val };
    onChange({ trials: next });
  };

  const addTrial = () => {
    const tn = (trials[trials.length - 1]?.trial_no || trials.length) + 1;
    onChange({ trials: [...trials, { trial_no: tn, levels: {}, results: {}, notes: "" }] });
  };
  const removeTrial = (idx) => onChange({ trials: trials.filter((_, i) => i !== idx) });

  // Compute average overall on the fly if all sub-scores set
  useEffect(() => {
    let dirty = false;
    const next = trials.map((t) => {
      const r = t.results || {};
      if (r.taste != null && r.texture != null && r.appearance != null && (r.overall == null || r._auto_overall)) {
        const ov = +(((+r.taste + +r.texture + +r.appearance) / 3).toFixed(2));
        if (r.overall !== ov) dirty = true;
        return { ...t, results: { ...r, overall: ov, _auto_overall: true } };
      }
      return t;
    });
    if (dirty) onChange({ trials: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(trials.map((t) => [t.results?.taste, t.results?.texture, t.results?.appearance]))]);

  if (factors.length < 2) {
    return (
      <div className="rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] py-10 text-center" data-testid="rnd-trials-empty">
        <FlaskConical size={28} className="mx-auto text-[#0A3B24]" />
        <div className="text-sm font-semibold text-[#111827] mt-2">Add factors first</div>
        <div className="text-xs text-[#4B5563] mt-1">Define factors then generate the experimental design to populate trials.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rnd-trials-table">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="kostra-section-title text-lg">Trial Execution Table</h3>
          <p className="text-xs text-[#4B5563] mt-1">
            Run each trial in your kitchen, score outputs, and record observations. Cost auto-fills if factors are linked to recipe ingredients.
          </p>
        </div>
        <Button type="button" onClick={addTrial} variant="outline" data-testid="rnd-add-trial"
                className="border-[#0A3B24]/30 text-[#0A3B24]">
          <Plus size={14} className="mr-1.5" /> Add Trial
        </Button>
      </div>
      <div className="overflow-x-auto kostra-card p-0">
        <table className="w-full text-xs">
          <thead className="bg-[#0A3B24] text-white">
            <tr>
              <th className="py-2 px-2 text-center font-semibold w-12">#</th>
              {factors.map((f) => (
                <th key={f.id} className="py-2 px-2 text-center font-semibold whitespace-nowrap">
                  {f.name} <span className="opacity-70 font-normal">({f.unit})</span>
                </th>
              ))}
              {RESPONSE_FIELDS.map((r) => (
                <th key={r.key} className="py-2 px-2 text-center font-semibold whitespace-nowrap">{r.label}</th>
              ))}
              <th className="py-2 px-2 text-center font-semibold whitespace-nowrap">Cost (₹/portion)</th>
              <th className="py-2 px-2 text-center font-semibold w-12"> </th>
            </tr>
          </thead>
          <tbody>
            {trials.map((t, i) => (
              <React.Fragment key={i}>
                <tr className={i % 2 === 0 ? "bg-white" : "bg-[#FAFBFC]"} data-testid={`rnd-trial-row-${i}`}>
                  <td className="py-1.5 px-2 text-center font-mono-num font-semibold text-[#0A3B24]">{t.trial_no || i + 1}</td>
                  {factors.map((f) => (
                    <td key={f.id} className="py-1 px-1.5">
                      <Input
                        type="number"
                        step={f.step || "any"}
                        value={(t.levels || {})[f.name] ?? ""}
                        onChange={(e) => updateLevel(i, f.name, e.target.value)}
                        className="h-9 font-mono-num text-center border"
                        data-testid={`rnd-trial-level-${i}-${f.name}`}
                      />
                    </td>
                  ))}
                  {RESPONSE_FIELDS.map((r) => (
                    <td key={r.key} className="py-1 px-1.5">
                      <Input
                        type="number"
                        min={r.min} max={r.max} step="0.1"
                        value={(t.results || {})[r.key] ?? ""}
                        onChange={(e) => updateResult(i, r.key, e.target.value)}
                        className="h-9 font-mono-num text-center border"
                        placeholder={`${r.min}-${r.max}`}
                        data-testid={`rnd-trial-result-${i}-${r.key}`}
                      />
                    </td>
                  ))}
                  <td className="py-1 px-1.5">
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        value={(t.results || {}).cost ?? ""}
                        onChange={(e) => updateResult(i, "cost", e.target.value)}
                        className="h-9 font-mono-num text-center border pr-8"
                        placeholder={project.base_recipe_snapshot ? "auto" : "manual"}
                        data-testid={`rnd-trial-cost-${i}`}
                      />
                      {(t.results || {})._cost_auto && (
                        <Lock size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#D4AF37]" title="Auto-computed from recipe" />
                      )}
                      {project.base_recipe_snapshot && !(t.results || {})._cost_auto && (
                        <button
                          type="button" title="Recompute from recipe"
                          onClick={() => liveCost(i)}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#0A3B24] hover:text-[#0E5232]"
                        >
                          <RotateCw size={12} className={autoCostBusy[i] ? "animate-spin" : ""} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-1 px-1 text-center align-top">
                    <button
                      type="button"
                      onClick={() => setEditingNotesIdx(editingNotesIdx === i ? null : i)}
                      className="text-[#0A3B24] hover:text-[#D4AF37] mr-1.5"
                      title="Edit notes"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTrial(i)}
                      className="text-[#4B5563] hover:text-[#DC2626]"
                      title="Remove trial"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
                {editingNotesIdx === i && (
                  <tr>
                    <td colSpan={factors.length + RESPONSE_FIELDS.length + 3} className="py-2 px-3 bg-[#FDF9EE] border-y border-[#D4AF37]/40">
                      <Label className="kostra-data-label mb-1 block">Trial notes / observations</Label>
                      <Textarea
                        value={t.notes || ""}
                        onChange={(e) => updateNotes(i, e.target.value)}
                        rows={2}
                        placeholder="Taste feedback, texture observation, issues, fixes..."
                        className="text-xs"
                        data-testid={`rnd-trial-notes-${i}`}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {trials.length === 0 && (
              <tr>
                <td colSpan={factors.length + RESPONSE_FIELDS.length + 3} className="py-6 text-center text-[#4B5563]">
                  No trials yet. Click <strong>Generate Design</strong> in the Setup tab to create a run table.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
